import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { maxUploadsForRequest, type AccountNumberState, type ClientUploadForClient, type InfoRequestForClient, type WithdrawnInfoRequestForClient } from '@steward-app/steward-types';
import { ApiError, isConnectivityError, userMessage } from '../../api/client';
import {
  INFO_REQUEST_NOT_FOUND, INFO_REQUEST_UPLOAD_LIMIT, ONBOARDING_DOCUMENT_CONTENT_MISMATCH, commitInfoRequestUpload, deleteInfoRequestUpload,
  mintInfoRequestUpload, onboardingFormQueryKey, type OnboardingFormView,
} from '../../api/onboardingForm';
import { UserText } from '../../components/UserText';
import { cls as formCls } from '../../components/formStyles';
import { btns } from '../../components/buttons';
import { CONTENT_MISMATCH_MESSAGE } from '../../lib/documentSignature';
import { uploadToS3 } from '../../lib/upload';
import { MAX_UPLOAD_BYTES, answerChange, checkUploadFile, rawAnswer, requestTitle } from './infoRequestsModel';
import { AccountNumberCard, type NumberFlush } from './AccountNumberCard';
import { WithdrawnRequests } from './WithdrawnRequests';
import type { Values } from './reviewAnswers';

/**
 * The "Requests from your advisor" step (crossmarc/steward-app#2284). It exists only when the
 * form carries at least one sent request (`shownPages`). A statement or document request takes
 * files, which go straight to the façade's three relays (mint, a PUT to the returned URL, commit)
 * and are not part of the autosaved answers; a question takes typed text, which IS part of them
 * (the `requests` section, see `infoRequestsModel.ts`). "Review and send" stays the only send.
 *
 * What is enforced where:
 *  - HERE, for a quick, kind message only (a page can be edited): the file type, read from the
 *    file's first bytes and never its name or `File.type` (steward-app#2327; the signatures are
 *    appcore's, read at appcore origin/main 6b02f97 on 2026-10-13), the size, and the per-request
 *    count.
 *  - The façade's mint relay (read 2026-10-10): checks the request is open, the per-request count
 *    and that the DECLARED size is at most 25 MB. It bounds the declared type to a short string
 *    and does not check it against the three allowed types. The count is the façade's alone.
 *  - appcore (read at origin/main 997f437 on 2026-10-10): owns the allow-list, binds the file
 *    name's extension to the declared type and, at COMMIT, measures the stored object
 *    (`HeadObject`) and refuses one that is empty or over 25 MB. The upload URL itself caps
 *    nothing, so an oversize object can be PUT before commit refuses it, and is then not
 *    reclaimed (crossmarc/appcore#828, open on 2026-10-10).
 * A request the server no longer treats as open answers one 404 whatever the reason.
 *
 * After a failure in which the commit MAY have run (the commit call errored, or the server said
 * the request is full), the form is read again and that request's files are replaced from it,
 * so the list shows what is stored, with a Remove control on each, when that read succeeds. When
 * it fails, the list stays as it was, the file error stays on the step, and the form stays on
 * screen (the form shows its load-error page only when it has never loaded). After every
 * successful add or remove the form's cached read is updated in place (`setQueryData`), so
 * leaving the step and coming back shows the same list without another request.
 */

export const UPLOAD_ERRORS = {
  type: 'That file type cannot be added. Please choose a PDF, JPG or PNG.',
  size: 'That file is larger than 25 MB. Please choose a smaller one.',
  empty: 'That file is empty. Please choose another one.',
  mismatch: CONTENT_MISMATCH_MESSAGE,
  closed: 'This request is no longer open. Please reload the page to see what is being asked now.',
  limit: 'This request already has all the files it takes. Remove one to add another.',
  add: 'That file could not be added. Please try again.',
  remove: 'That file could not be removed. Please try again.',
} as const;

export const STATEMENT_HINT = `Add one recent statement (PDF, JPG or PNG, up to ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB).`;
export const documentHint = (max: number) => `Add up to ${max} files (PDF, JPG or PNG, up to ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB each).`;
export const NUMBERS_HIDDEN_NOTE = 'Long numbers are hidden in your answer, so please do not type an account number here. To share one, ask your advisor to request a document that shows it. If this was a date or a phone number, try writing it another way.';
export const ANSWER_SINGLE_PARAGRAPH_HINT = 'Your answer is saved as a single paragraph.';
export const ANSWER_CAP_ERROR = 'Your answers have reached the most we can save. Please clear another answer to make room for this one.';
export const ANSWER_CLIPPED_NOTE = 'That was longer than we can keep, so the end was left off.';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

export interface UploadsApi {
  uploads: Record<string, ClientUploadForClient[]>;
  busy: Record<string, 'adding' | 'removing' | undefined>;
  errors: Record<string, string | undefined>;
  /** A short sentence for a screen reader ("File added."), or ''. `seq` changes with every
   *  announcement, so the same words twice in a row are announced twice. */
  announcement: { text: string; seq: number };
  add: (requestId: string, files: File[]) => Promise<void>;
  remove: (requestId: string, documentId: string) => Promise<void>;
}

/** The files added under each request, and the actions that change them. Lives in the form's body
 *  so the rail, the review and the step read one list. */
export function useInfoRequestUploads(token: string, firmWorkspaceId: string, requests: readonly InfoRequestForClient[]): UploadsApi {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<Record<string, ClientUploadForClient[]>>(
    () => Object.fromEntries(requests.map((r) => [r.id, r.documents ?? []])),
  );
  const [busy, setBusy] = useState<UploadsApi['busy']>({});
  const [errors, setErrors] = useState<UploadsApi['errors']>({});
  const [announcement, setAnnouncement] = useState({ text: '', seq: 0 });
  const inFlight = useRef(new Set<string>());
  // The list as the latest add/remove/refetch left it. A ref, so a loop of several files sees each
  // file it has just stored (state would still hold the list from the render before).
  const uploadsRef = useRef(uploads);
  const kindOf = useCallback((id: string) => requests.find((r) => r.id === id)?.kind, [requests]);
  const announce = (text: string) => setAnnouncement((a) => ({ text, seq: a.seq + 1 }));

  /** Sets one request's files in the hook's state AND in the form's cached read. The cache keeps
   *  `documents` absent when there are none, as the server does. A cache with no data yet is left
   *  alone (an updater that returns undefined makes no change). */
  const setFiles = useCallback((requestId: string, docs: ClientUploadForClient[]) => {
    uploadsRef.current = { ...uploadsRef.current, [requestId]: docs };
    setUploads(uploadsRef.current);
    queryClient.setQueryData<OnboardingFormView>(onboardingFormQueryKey(firmWorkspaceId), (old) => {
      if (!old?.requests) return old;
      return {
        ...old,
        requests: old.requests.map((r) => {
          if (r.id !== requestId) return r;
          const { documents: _was, ...rest } = r;
          return docs.length > 0 ? { ...rest, documents: docs } : rest;
        }),
      };
    });
    // setQueryData marks the query fresh. The form marks it stale (without a refetch) when a save
    // changes what the read would return (OnboardingForm.tsx, the tax-id save), so mark it stale
    // again, still without a refetch.
    void queryClient.invalidateQueries({ queryKey: onboardingFormQueryKey(firmWorkspaceId), refetchType: 'none' });
  }, [queryClient, firmWorkspaceId]);

  /** Reads the form again and takes that request's files from it. It refetches the form's own
   *  query (the form component that holds this step declares it; a second `queryFn` for the same
   *  key is what steward-app#673's test forbids) and reads the result back from the cache. A
   *  failed read, or no query to refetch, changes nothing here and returns undefined. The failed
   *  fetch leaves the query in an error state that still holds its data, and the form shows its
   *  load-error page only when it holds none, so the client keeps the form. */
  const resync = useCallback(async (requestId: string): Promise<ClientUploadForClient[] | undefined> => {
    try {
      const queryKey = onboardingFormQueryKey(firmWorkspaceId);
      await queryClient.refetchQueries({ queryKey, exact: true }, { throwOnError: true });
      const hit = queryClient.getQueryData<OnboardingFormView>(queryKey)?.requests?.find((r) => r.id === requestId);
      if (!hit) return undefined;
      const docs = hit.documents ?? [];
      uploadsRef.current = { ...uploadsRef.current, [requestId]: docs };
      setUploads(uploadsRef.current);
      return docs;
    } catch { return undefined; }
  }, [queryClient, firmWorkspaceId]);

  // Only messages written for the client are shown. An error from the upload itself ("S3 upload
  // failed: 403"), a server fault or an unexpected throw is not, so those get the fixed sentence;
  // a lost connection gets the app's own connectivity sentence.
  const messageFor = (err: unknown, fallback: string) =>
    err instanceof ApiError && err.code === INFO_REQUEST_NOT_FOUND ? UPLOAD_ERRORS.closed
      : err instanceof ApiError && err.code === INFO_REQUEST_UPLOAD_LIMIT ? UPLOAD_ERRORS.limit
        : err instanceof ApiError && err.code === ONBOARDING_DOCUMENT_CONTENT_MISMATCH ? UPLOAD_ERRORS.mismatch
        : isConnectivityError(err) ? userMessage(err, fallback) : fallback;
  const setError = (requestId: string, message: string | undefined) => setErrors((e) => ({ ...e, [requestId]: message }));

  const add = useCallback(async (requestId: string, files: File[]) => {
    const kind = kindOf(requestId);
    if (!kind || inFlight.current.has(requestId)) return;
    inFlight.current.add(requestId);
    setError(requestId, undefined);
    setBusy((b) => ({ ...b, [requestId]: 'adding' }));
    try {
      for (const file of files) {
        if ((uploadsRef.current[requestId]?.length ?? 0) >= maxUploadsForRequest(kind)) {
          setError(requestId, UPLOAD_ERRORS.limit);
          break;
        }
        const check = await checkUploadFile(file);
        if (!check.ok) { setError(requestId, UPLOAD_ERRORS[check.reason]); break; }
        let commitMayHaveRun = false;
        const before = uploadsRef.current[requestId]?.length ?? 0;
        try {
          const { uploadUrl, entityId } = await mintInfoRequestUpload(token, firmWorkspaceId, requestId, {
            filename: check.filename, mimeType: check.mimeType, sizeBytes: file.size,
          });
          // Re-wrapped so the PUT's Content-Type is the type the mint was told, whatever the browser guessed.
          await uploadToS3(uploadUrl, new File([file], check.filename, { type: check.mimeType }), () => {});
          commitMayHaveRun = true;
          const doc = await commitInfoRequestUpload(token, firmWorkspaceId, requestId, {
            entityId, filename: check.filename, mimeType: check.mimeType,
          });
          setFiles(requestId, [...(uploadsRef.current[requestId] ?? []), doc]);
          announce('File added.');
        } catch (err) {
          const message = messageFor(err, UPLOAD_ERRORS.add);
          const full = err instanceof ApiError && err.code === INFO_REQUEST_UPLOAD_LIMIT;
          if (commitMayHaveRun || full) {
            // The commit may have stored the file even though its answer never arrived (or the
            // server counts more files than this page does): show what is actually stored.
            const stored = await resync(requestId);
            if (stored && stored.length > before && !full) { announce('File added.'); break; }
          }
          setError(requestId, message);
          break;
        }
      }
    } finally {
      inFlight.current.delete(requestId);
      setBusy((b) => ({ ...b, [requestId]: undefined }));
    }
  }, [token, firmWorkspaceId, kindOf, setFiles, resync]);

  const remove = useCallback(async (requestId: string, documentId: string) => {
    if (inFlight.current.has(requestId)) return;
    inFlight.current.add(requestId);
    setError(requestId, undefined);
    setBusy((b) => ({ ...b, [requestId]: 'removing' }));
    try {
      await deleteInfoRequestUpload(token, firmWorkspaceId, requestId, documentId);
      setFiles(requestId, (uploadsRef.current[requestId] ?? []).filter((d) => d.id !== documentId));
      announce('File removed.');
    } catch (err) { setError(requestId, messageFor(err, UPLOAD_ERRORS.remove)); } finally {
      inFlight.current.delete(requestId);
      setBusy((b) => ({ ...b, [requestId]: undefined }));
    }
  }, [token, firmWorkspaceId, setFiles]);

  return { uploads, busy, errors, announcement, add, remove };
}

function fileSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Said under a request that cannot take another file, where the button is off. */
export const FULL_NOTE = 'This request has all the files it takes. Remove one to add another.';

/**
 * One statement or document request: the files added so far, each with a Remove, and a styled
 * button that opens the file picker (steward-app#2433, #54). The button is the same control as the
 * snapshot form's "Upload a statement" (`cls.btn`); the real `<input type="file">` is hidden and
 * opened by it, so the picker is one control to a keyboard or a screen reader. The button carries
 * the request's name, because several "Add a file" buttons on one page tell nobody which is which.
 *
 * Shared by the form's requests step and the standalone requests page, which differ only in
 * what they call the card (`title`) and the line under it (`hint`). `locked` turns Add and Remove off
 * while the page is finishing, so no upload can start after Done has begun waiting on its saves.
 */
export function FileRequest({ request, api, title, hint, locked }: { request: InfoRequestForClient; api: UploadsApi; title?: string; hint?: string; locked?: boolean }) {
  const files = api.uploads[request.id] ?? [];
  const max = maxUploadsForRequest(request.kind);
  const full = files.length >= max;
  const busy = api.busy[request.id];
  const error = api.errors[request.id];
  const name = title ?? requestTitle(request);
  const inputId = `onb-req-file-${request.id}`;
  const hintId = `onb-req-hint-${request.id}`;
  const errId = `onb-req-err-${request.id}`;
  const root = useRef<HTMLFieldSetElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const addLabel = request.kind === 'statement' ? 'Add a statement' : 'Add a file';
  // After an add or a remove, once the work has finished, focus goes back into the request: to the
  // Add button, or to the first Remove button when the request is full and the Add button is off
  // (which is also where a failed remove on a full request leaves it).
  const [refocus, setRefocus] = useState(false);
  useEffect(() => {
    if (!refocus || busy) return;
    setRefocus(false);
    const target = full ? root.current?.querySelector<HTMLElement>('button[data-remove]') : root.current?.querySelector<HTMLElement>('button[data-add]');
    target?.focus();
  }, [refocus, busy, full]);
  return (
    <fieldset ref={root} className="border border-line rounded-token p-4 mb-4" data-testid={`onb-req-${request.id}`}>
      <legend className="px-1 text-[14px] font-sans text-navy"><UserText>{name}</UserText></legend>
      <p id={hintId} className="text-[12px] text-mid font-sans m-0 mb-3">
        {hint ?? (request.kind === 'statement' ? STATEMENT_HINT : documentHint(max))}
      </p>
      {files.length > 0 && (
        <ul className="list-none p-0 m-0 mb-3" aria-label={`Files added for ${name}`}>
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 border-b border-line last:border-b-0 py-2 text-[13px] font-sans text-navy">
              <span className="min-w-0 break-words"><UserText>{f.title}</UserText> <span className="text-mid">({fileSize(f.fileSize)})</span></span>
              <button type="button" className={`${btns.btnSm} max-sm:min-h-11`} disabled={busy !== undefined || locked} data-remove=""
                aria-label={`Remove ${f.title}`}
                onClick={() => { setRefocus(true); void api.remove(request.id, f.id); }}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" data-add="" className={`${formCls.btn} disabled:cursor-default disabled:opacity-40`}
        disabled={full || busy !== undefined || locked}
        aria-label={`${addLabel} for ${name}`}
        aria-describedby={error ? `${hintId} ${errId}` : hintId}
        onClick={() => input.current?.click()}>
        {addLabel}
      </button>
      <input ref={input} id={inputId} type="file" accept={ACCEPT} multiple={max > 1} className="hidden" tabIndex={-1}
        data-testid={`onb-req-input-${request.id}`}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (picked.length > 0) { setRefocus(true); void api.add(request.id, picked); }
        }} />
      {full && <p className="text-[12px] text-mid font-sans m-0 mt-2">{FULL_NOTE}</p>}
      {busy === 'adding' && <p className="text-[12px] text-slate font-sans m-0 mt-2">Adding...</p>}
      {busy === 'removing' && <p className="text-[12px] text-slate font-sans m-0 mt-2">Removing...</p>}
      {error && <p id={errId} className={formCls.error} role="alert">{error}</p>}
    </fieldset>
  );
}

export function QuestionRequest({ request, values, onAnswer }: {
  request: InfoRequestForClient; values: Values; onAnswer: (requestId: string, text: string, clean?: boolean) => boolean;
}) {
  const id = `onb-req-answer-${request.id}`;
  const hintId = `${id}-hint`;
  const noteId = `${id}-note`;
  const capId = `${id}-cap`;
  const [note, setNote] = useState<'numbers' | 'long' | null>(null);
  const [capReached, setCapReached] = useState(false);
  return (
    <div className={formCls.field} data-testid={`onb-req-${request.id}`}>
      <label htmlFor={id} className={formCls.label}><UserText>{requestTitle(request)}</UserText></label>
      <textarea id={id} className={formCls.textarea} rows={4} value={rawAnswer(values, request.id)}
        aria-describedby={`${hintId} ${noteId}${capReached ? ` ${capId}` : ''}`} aria-invalid={capReached ? true : undefined}
        onChange={(e) => { setNote(null); setCapReached(!onAnswer(request.id, e.target.value)); }}
        onBlur={(e) => {
          // Show the client what will actually be kept, and say so when it differs.
          const raw = e.target.value;
          const change = answerChange(raw);
          setNote(change);
          if (change !== null) setCapReached(!onAnswer(request.id, raw, true));
        }} />
      <p id={hintId} className="text-[12px] text-mid font-sans m-0 mt-1">{ANSWER_SINGLE_PARAGRAPH_HINT}</p>
      {/* The status container is always in the page, so a note put into it is announced. */}
      <p id={noteId} className="text-[12px] text-mid font-sans m-0 mt-1 empty:hidden" role="status">
        {note === 'numbers' ? NUMBERS_HIDDEN_NOTE : note === 'long' ? ANSWER_CLIPPED_NOTE : ''}
      </p>
      {capReached && <p id={capId} className={formCls.error} role="alert">{ANSWER_CAP_ERROR}</p>}
    </div>
  );
}

export function InfoRequestsStep({ firm, note, requests, withdrawn = [], api, values, onAnswer, numbers }: {
  firm: ReactNode;
  note: string | undefined;
  requests: readonly InfoRequestForClient[];
  withdrawn?: readonly WithdrawnInfoRequestForClient[];
  api: UploadsApi;
  values: Values;
  /** `clean` true means store the cleaned text in the box (the field was left). */
  /** `clean` true means store the cleaned text in the box (the field was left). Returns false
   *  when the change could not be kept (the answers are at their cap). */
  onAnswer: (requestId: string, text: string, clean?: boolean) => boolean;
  /** What the account-number cards need (docs/38 §5): the server's masked states, and who is asking. */
  numbers?: {
    token: string; firmWorkspaceId: string; states: readonly AccountNumberState[] | undefined;
    onClosed: () => void; registerFlush: (requestId: string, flush: NumberFlush | null) => void;
  };
}) {
  return (
    <>
      <p className="text-[13px] text-mid font-sans mt-0 mb-5">
        {firm} would like a few more things from you. Add what you can now, and the rest later. None of it is needed to send your form.
      </p>
      {note && (
        <div className="mb-5 border-l-2 border-line pl-3" data-testid="onb-req-note">
          <p className="text-[11px] uppercase tracking-[0.08em] text-slate font-sans m-0 mb-1">A note from your advisor</p>
          <p className="text-[13px] text-navy font-sans m-0 whitespace-pre-wrap break-words"><UserText>{note}</UserText></p>
        </div>
      )}
      {requests.map((r) => (r.kind === 'question'
        ? <QuestionRequest key={r.id} request={r} values={values} onAnswer={onAnswer} />
        : r.kind === 'account-number'
          // Never a file card: this kind takes no file. Without `numbers` there is nothing to draw.
          ? (numbers ? <AccountNumberCard key={r.id} request={r} state={numbers.states?.find((s) => s.requestId === r.id)} token={numbers.token}
              firmWorkspaceId={numbers.firmWorkspaceId} firm={firm} onClosed={numbers.onClosed} registerFlush={numbers.registerFlush} /> : null)
          : <FileRequest key={r.id} request={r} api={api} />))}
      <WithdrawnRequests firmCap={firm} withdrawn={withdrawn} testId="onb-req-withdrawn" />
      <p role="status" className="sr-only"><span key={api.announcement.seq}>{api.announcement.text}</span></p>
    </>
  );
}
